import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search,
  Users,
  Building2,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Calculator,
  Loader2,
  FileSpreadsheet,
  FileImage,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { useExport } from '../hooks/useExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);
};

const HeadCountPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchNome, setSearchNome] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [filterRegime, setFilterRegime] = useState('all');
  const [expandedEmpresa, setExpandedEmpresa] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [expandedDetailSections, setExpandedDetailSections] = useState({ beneficios: false, encargos: false });

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('excel');
  const [selectedCompanies, setSelectedCompanies] = useState(new Set());
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  const [exportSearch, setExportSearch] = useState('');
  const [expandedExportEmpresa, setExpandedExportEmpresa] = useState(null);
  const [exportRegimeFilter, setExportRegimeFilter] = useState('all');

  const { exportToExcel, exportToExcelMultiSheet, exportToPDF, exporting } = useExport();

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchNome);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchNome]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchDebounce) params.append('nome', searchDebounce);
      if (filterRegime && filterRegime !== 'all') params.append('regime', filterRegime);

      const response = await axios.get(`${API}/headcount/employee-costs?${params}`);
      setCompanies(response.data);
    } catch (error) {
      console.error('Erro ao carregar custos:', error);
      toast.error('Erro ao carregar dados de custos');
    } finally {
      setLoading(false);
    }
  }, [searchDebounce, filterRegime]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalGeral = companies.reduce((acc, c) => acc + c.custo_total_empresa, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">HeadCount - Custo por Empresa</h1>
          <p className="text-slate-600 mt-1">
            Custo total de cada funcionário para a empresa (salário + benefícios + encargos)
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Card className="bg-green-50 border-green-200">
            <CardContent className="py-3 px-6">
              <p className="text-sm text-green-700 font-medium">Custo Total Geral</p>
              <p className="text-2xl font-bold text-green-800">{formatCurrency(totalGeral)}</p>
            </CardContent>
          </Card>
          <Button
            onClick={() => setExportModalOpen(true)}
            className="flex items-center gap-2"
            disabled={companies.length === 0}
          >
            <Download className="h-4 w-4" />
            Exportar
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="Buscar por nome do funcionário..."
                value={searchNome}
                onChange={(e) => setSearchNome(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="w-48">
              <Select value={filterRegime} onValueChange={(v) => { setFilterRegime(v); setExpandedEmpresa(null); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os regimes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os regimes</SelectItem>
                  <SelectItem value="CLT">CLT</SelectItem>
                  <SelectItem value="PJ">PJ</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="flex items-center gap-2 px-3 py-1.5 whitespace-nowrap">
              <Building2 className="h-3.5 w-3.5" />
              {companies.length} {companies.length === 1 ? 'empresa' : 'empresas'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {companies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            {searchNome || filterRegime
              ? 'Nenhum funcionário encontrado com os filtros aplicados'
              : 'Nenhuma empresa com funcionários ativos cadastrados'}
          </CardContent>
        </Card>
      ) : (
        companies.map((company) => {
          const isExpanded = expandedEmpresa === company.empresa;
          return (
            <Card key={company.empresa} className="overflow-hidden w-full">
              <div
                className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedEmpresa(isExpanded ? null : company.empresa)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Building2 className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <span className="font-semibold text-lg text-slate-800 truncate" title={company.empresa}>
                        {company.empresa.length > 20 ? company.empresa.slice(0, 20) + '...' : company.empresa}
                      </span>
                      {company.regime_tributario && (
                        <Badge variant="secondary" className="text-xs flex-shrink-0">
                          {company.regime_tributario}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" />
                        {company.total_funcionarios} {company.total_funcionarios === 1 ? 'funcionário' : 'funcionários'}
                      </span>
                      <span>{company.total_clt} CLT</span>
                      <span>{company.total_pj} PJ</span>
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <p className="text-sm text-slate-500">Custo Total</p>
                  <p className="text-xl font-bold text-green-700">
                    {formatCurrency(company.custo_total_empresa)}
                  </p>
                </div>
              </div>

              {isExpanded && (
                <div className="border-t">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Funcionário</TableHead>
                          <TableHead>Cargo</TableHead>
                          <TableHead>Regime</TableHead>
                          <TableHead className="text-right">Salário</TableHead>
                          <TableHead className="text-right">Benefícios</TableHead>
                          <TableHead className="text-right">Encargos</TableHead>
                          <TableHead className="text-right">Custo Total</TableHead>
                          <TableHead className="text-center">Detalhes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {company.funcionarios.map((emp) => (
                          <TableRow key={emp.employee_id}>
                            <TableCell className="font-medium">{emp.nome}</TableCell>
                            <TableCell className="text-slate-600">{emp.cargo || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={emp.regime === 'CLT' ? 'default' : 'outline'}>
                                {emp.regime}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(emp.salario)}
                            </TableCell>
                            <TableCell className="text-right text-purple-700">
                              {formatCurrency(emp.total_beneficios)}
                            </TableCell>
                            <TableCell className="text-right text-amber-700">
                              {formatCurrency(emp.total_encargos)}
                            </TableCell>
                            <TableCell className="text-right font-bold text-green-700">
                              {formatCurrency(emp.custo_total)}
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedEmployee(emp);
                                  setExpandedDetailSections({ beneficios: false, encargos: false });
                                }}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                title="Ver detalhamento"
                              >
                                <Calculator className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="flex items-center justify-end gap-8 px-6 py-3 bg-slate-50 border-t text-sm">
                    <div>
                      <span className="text-slate-500">Folha Salarial: </span>
                      <span className="font-medium text-slate-700">{formatCurrency(company.folha_salarial)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Total Benefícios: </span>
                      <span className="font-medium text-purple-700">{formatCurrency(company.folha_beneficios)}</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Total Encargos: </span>
                      <span className="font-medium text-amber-700">{formatCurrency(company.folha_encargos)}</span>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })
      )}

      <Dialog open={!!selectedEmployee} onOpenChange={(open) => { if (!open) { setSelectedEmployee(null); setExpandedDetailSections({ beneficios: false, encargos: false }); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedEmployee && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  {selectedEmployee.nome}
                </DialogTitle>
                <div className="flex items-center gap-3 text-sm text-slate-500 mt-1 flex-wrap">
                  {selectedEmployee.cargo && (
                    <span className="flex items-center gap-1">
                      <Briefcase className="h-3.5 w-3.5" />
                      {selectedEmployee.cargo}
                    </span>
                  )}
                  {selectedEmployee.departamento && (
                    <span>{selectedEmployee.departamento}</span>
                  )}
                  <Badge variant={selectedEmployee.regime === 'CLT' ? 'default' : 'outline'}>
                    {selectedEmployee.regime}
                  </Badge>
                </div>
              </DialogHeader>

              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="py-3 px-4">
                      <p className="text-xs text-blue-600 font-medium">Salário</p>
                      <p className="text-lg font-bold text-blue-800">
                        {formatCurrency(selectedEmployee.salario)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50 border-purple-200">
                    <CardContent className="py-3 px-4">
                      <p className="text-xs text-purple-600 font-medium">Benefícios</p>
                      <p className="text-lg font-bold text-purple-800">
                        {formatCurrency(selectedEmployee.total_beneficios)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50 border-amber-200">
                    <CardContent className="py-3 px-4">
                      <p className="text-xs text-amber-600 font-medium">Encargos</p>
                      <p className="text-lg font-bold text-amber-800">
                        {formatCurrency(selectedEmployee.total_encargos)}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                <Card className={selectedEmployee.custo_total > 0 ? 'bg-green-50 border-green-300' : ''}>
                  <CardContent className="py-4 px-4 flex items-center justify-between">
                    <span className="text-base font-bold text-green-800">CUSTO TOTAL</span>
                    <span className="text-2xl font-bold text-green-800">
                      {formatCurrency(selectedEmployee.custo_total)}
                    </span>
                  </CardContent>
                </Card>

                {(() => {
                  const items = selectedEmployee.itens_detalhados || [];
                  const salarioItem = items.find(i => i.tipo === 'salario');
                  const beneficioItems = items.filter(i => i.tipo === 'beneficio');
                  const encargoItems = items.filter(i => i.tipo === 'encargo' && i.label !== 'CUSTO TOTAL');
                  const custoTotalItem = items.find(i => i.label === 'CUSTO TOTAL');
                  const totalBeneficio = beneficioItems.find(i => i.label === 'Total Benefícios');
                  const totalEncargo = encargoItems.find(i => i.label === 'Total Encargos');
                  const itensBeneficio = beneficioItems.filter(i => i.label !== 'Total Benefícios');
                  const itensEncargo = encargoItems.filter(i => i.label !== 'Total Encargos');

                  const toggleSection = (section) => {
                    setExpandedDetailSections(prev => ({ ...prev, [section]: !prev[section] }));
                  };

                  return (
                    <div className="divide-y rounded-lg border">
                      {salarioItem && (
                        <div className="flex items-center justify-between px-4 py-3 bg-blue-50/50">
                          <span className="font-semibold text-slate-800">Salário Base</span>
                          <span className="font-medium text-blue-800">{formatCurrency(salarioItem.valor)}</span>
                        </div>
                      )}

                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => toggleSection('beneficios')}
                      >
                        <div className="flex items-center gap-2">
                          {expandedDetailSections.beneficios ? (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          )}
                          <span className="font-medium text-purple-700">Total Benefícios</span>
                        </div>
                        <span className="font-medium text-purple-800">
                          {totalBeneficio ? formatCurrency(totalBeneficio.valor) : '-'}
                        </span>
                      </div>

                      {expandedDetailSections.beneficios && itensBeneficio.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between px-4 py-2 pl-10 text-sm text-slate-600"
                        >
                          <span>{item.label}</span>
                          <span>{formatCurrency(item.valor)}</span>
                        </div>
                      ))}

                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => toggleSection('encargos')}
                      >
                        <div className="flex items-center gap-2">
                          {expandedDetailSections.encargos ? (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-slate-400" />
                          )}
                          <span className="font-medium text-amber-700">Total Encargos</span>
                        </div>
                        <span className="font-medium text-amber-800">
                          {totalEncargo ? formatCurrency(totalEncargo.valor) : '-'}
                        </span>
                      </div>

                      {expandedDetailSections.encargos && itensEncargo.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between px-4 py-2 pl-10 text-sm text-slate-600"
                        >
                          <span>{item.label}</span>
                          <span>{formatCurrency(item.valor)}</span>
                        </div>
                      ))}

                      {custoTotalItem && (
                        <div className="flex items-center justify-between px-4 py-4 bg-green-100 font-bold">
                          <span className="text-green-800">CUSTO TOTAL</span>
                          <span className="text-lg text-green-800">{formatCurrency(custoTotalItem.valor)}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={exportModalOpen} onOpenChange={(open) => { if (!open) { setExportModalOpen(false); setExportSearch(''); setExpandedExportEmpresa(null); } }}>
        <DialogContent className="w-auto min-w-[500px] max-w-[90vw] max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-600" />
              Exportar HeadCount
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex gap-4">
              <div>
                <Label>Formato de exportação</Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    variant={exportFormat === 'excel' ? 'default' : 'outline'}
                    onClick={() => setExportFormat('excel')}
                    className="flex items-center gap-2"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    Excel
                  </Button>
                  <Button
                    variant={exportFormat === 'pdf' ? 'default' : 'outline'}
                    onClick={() => setExportFormat('pdf')}
                    className="flex items-center gap-2"
                  >
                    <FileImage className="h-4 w-4" />
                    PDF
                  </Button>
                </div>
              </div>
              <div>
                <Label>Filtrar por regime</Label>
                <Select value={exportRegimeFilter} onValueChange={setExportRegimeFilter}>
                  <SelectTrigger className="w-48 mt-1">
                    <SelectValue placeholder="Todos os regimes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os regimes</SelectItem>
                    <SelectItem value="CLT">CLT</SelectItem>
                    <SelectItem value="PJ">PJ</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Buscar funcionário para exportar..."
                  value={exportSearch}
                  onChange={(e) => setExportSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const allEmpIds = new Set();
                  const allFuncIds = new Set();
                  companies.forEach(c => {
                    allEmpIds.add(c.empresa);
                    c.funcionarios.forEach(f => allFuncIds.add(f.employee_id));
                  });
                  if (selectedCompanies.size === allEmpIds.size && selectedEmployees.size === allFuncIds.size) {
                    setSelectedCompanies(new Set());
                    setSelectedEmployees(new Set());
                  } else {
                    setSelectedCompanies(allEmpIds);
                    setSelectedEmployees(allFuncIds);
                  }
                }}
              >
                {selectedCompanies.size === companies.reduce((a, c) => a + c.funcionarios.length, 0) ? 'Limpar seleção' : 'Selecionar tudo'}
              </Button>
            </div>

            <ScrollArea className="max-h-[50vh] pr-4">
              <div className="space-y-3">
                {companies.map((company) => {
                  const filteredFuncs = company.funcionarios.filter(f =>
                    (!exportSearch || f.nome.toLowerCase().includes(exportSearch.toLowerCase())) &&
                    (exportRegimeFilter === 'all' || f.regime === exportRegimeFilter)
                  );
                  const isCompanySelected = selectedCompanies.has(company.empresa);
                  const allCompanyFuncsSelected = filteredFuncs.length > 0 && filteredFuncs.every(f => selectedEmployees.has(f.employee_id));

                  if (exportSearch && filteredFuncs.length === 0) return null;

                  return (
                    <Card key={company.empresa} className="overflow-hidden">
                      <div
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors min-w-0"
                        onClick={() => setExpandedExportEmpresa(expandedExportEmpresa === company.empresa ? null : company.empresa)}
                      >
                        <Checkbox
                          checked={isCompanySelected && allCompanyFuncsSelected}
                          onCheckedChange={(checked) => {
                            const newSelectedCompanies = new Set(selectedCompanies);
                            const newSelectedEmployees = new Set(selectedEmployees);
                            if (checked) {
                              newSelectedCompanies.add(company.empresa);
                              company.funcionarios.forEach(f => newSelectedEmployees.add(f.employee_id));
                            } else {
                              newSelectedCompanies.delete(company.empresa);
                              company.funcionarios.forEach(f => newSelectedEmployees.delete(f.employee_id));
                            }
                            setSelectedCompanies(newSelectedCompanies);
                            setSelectedEmployees(newSelectedEmployees);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <span className="font-medium text-slate-800 flex-1 truncate">
                          {company.empresa}
                        </span>
                        <span className="text-sm text-slate-500">
                          {filteredFuncs.length} {filteredFuncs.length === 1 ? 'funcionário' : 'funcionários'}
                        </span>
                        {expandedExportEmpresa === company.empresa ? (
                          <ChevronDown className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        )}
                      </div>

                      {expandedExportEmpresa === company.empresa && (
                        <div className="border-t divide-y">
                          {filteredFuncs.map((func) => (
                            <div
                              key={func.employee_id}
                              className="flex items-center gap-3 px-4 py-2 pl-12 hover:bg-slate-50 transition-colors min-w-0"
                            >
                              <Checkbox
                                checked={selectedEmployees.has(func.employee_id)}
                                onCheckedChange={(checked) => {
                                  const newSet = new Set(selectedEmployees);
                                  if (checked) {
                                    newSet.add(func.employee_id);
                                  } else {
                                    newSet.delete(func.employee_id);
                                  }
                                  setSelectedEmployees(newSet);
                                }}
                              />
                              <span className="flex-1 text-sm text-slate-700">{func.nome}</span>
                              <Badge variant={func.regime === 'CLT' ? 'default' : 'outline'} className="text-xs">
                                {func.regime}
                              </Badge>
                              <span className="text-sm font-medium text-green-700">
                                {formatCurrency(func.custo_total)}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setExportModalOpen(false); setExportSearch(''); setExpandedExportEmpresa(null); }}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                const filename = `headcount_${new Date().toISOString().split('T')[0]}`;
                const selectedData = [];

                companies.forEach(company => {
                  company.funcionarios.forEach(func => {
                    if (selectedEmployees.has(func.employee_id)) {
                      selectedData.push({
                        empresa: company.empresa,
                        regime_tributario: company.regime_tributario,
                        nome: func.nome,
                        cargo: func.cargo || '-',
                        regime: func.regime,
                        salario: func.salario,
                        beneficios: func.total_beneficios,
                        encargos: func.total_encargos,
                        custo_total: func.custo_total,
                        itens_detalhados: func.itens_detalhados || [],
                      });
                    }
                  });
                });

                if (selectedData.length === 0) {
                  toast.error('Selecione ao menos um funcionário para exportar');
                  return;
                }

                if (exportFormat === 'excel') {
                  const allLabels = [];
                  const labelSet = new Set();
                  selectedData.forEach(row => {
                    row.itens_detalhados.forEach(item => {
                      if (!labelSet.has(item.label)) {
                        labelSet.add(item.label);
                        allLabels.push(item.label);
                      }
                    });
                  });

                  const detalhadoRows = selectedData.map(row => {
                    const itemMap = {};
                    row.itens_detalhados.forEach(item => {
                      itemMap[item.label] = item.valor;
                    });
                    const obj = {
                      'Empresa': row.empresa,
                      'Regime Tributário': row.regime_tributario,
                      'Funcionário': row.nome,
                      'Cargo': row.cargo,
                      'Regime': row.regime,
                    };
                    allLabels.forEach(label => {
                      obj[label] = itemMap[label] ?? 0;
                    });
                    return obj;
                  });

                  const companyGroups = {};
                  selectedData.forEach(row => {
                    if (!companyGroups[row.empresa]) {
                      companyGroups[row.empresa] = {
                        empresa: row.empresa,
                        regime_tributario: row.regime_tributario,
                        total_funcionarios: 0,
                        sums: {},
                      };
                    }
                    const group = companyGroups[row.empresa];
                    group.total_funcionarios++;
                    row.itens_detalhados.forEach(item => {
                      group.sums[item.label] = (group.sums[item.label] || 0) + item.valor;
                    });
                  });

                  const resumoRows = Object.values(companyGroups).map(group => {
                    const obj = {
                      'Empresa': group.empresa,
                      'Regime Tributário': group.regime_tributario,
                      'Total Funcionários': group.total_funcionarios,
                    };
                    allLabels.forEach(label => {
                      obj[label] = group.sums[label] ?? 0;
                    });
                    return obj;
                  });

                  exportToExcelMultiSheet([
                    { name: 'Detalhado', data: detalhadoRows },
                    { name: 'Resumo', data: resumoRows },
                  ], filename);
                } else {
                  const allLabels = [];
                  const labelSet = new Set();
                  selectedData.forEach(row => {
                    (row.itens_detalhados || []).forEach(item => {
                      if (!labelSet.has(item.label)) {
                        labelSet.add(item.label);
                        allLabels.push(item.label);
                      }
                    });
                  });

                  const pdfRows = selectedData.map(row => {
                    const itemMap = {};
                    (row.itens_detalhados || []).forEach(item => {
                      itemMap[item.label] = item.valor;
                    });
                    const obj = {
                      empresa: row.empresa,
                      regime_tributario: row.regime_tributario,
                      nome: row.nome,
                      cargo: row.cargo,
                      regime: row.regime,
                    };
                    allLabels.forEach(label => {
                      obj[label] = itemMap[label] ?? 0;
                    });
                    return obj;
                  });

                  const fixedColumns = [
                    { header: 'Empresa', dataKey: 'empresa' },
                    { header: 'Regime Tributário', dataKey: 'regime_tributario' },
                    { header: 'Funcionário', dataKey: 'nome' },
                    { header: 'Cargo', dataKey: 'cargo' },
                    { header: 'Regime', dataKey: 'regime' },
                  ];
                  const detailColumns = allLabels.map(label => ({
                    header: label,
                    dataKey: label,
                  }));
                  const columns = [...fixedColumns, ...detailColumns];

                  const totalFuncionarios = selectedData.length;
                  const totalCusto = selectedData.reduce((sum, r) => sum + (r.custo_total || 0), 0);
                  const totalSalarios = selectedData.reduce((sum, r) => sum + (r.salario || 0), 0);
                  const totalBeneficios = selectedData.reduce((sum, r) => sum + (r.beneficios || 0), 0);
                  const totalEncargos = selectedData.reduce((sum, r) => sum + (r.encargos || 0), 0);

                  const summaryData = [
                    { label: 'Total de Funcionários Exportados', valor: String(totalFuncionarios) },
                    { label: 'Folha Salarial Total', valor: formatCurrency(totalSalarios) },
                    { label: 'Total Benefícios', valor: formatCurrency(totalBeneficios) },
                    { label: 'Total Encargos', valor: formatCurrency(totalEncargos) },
                    { label: 'CUSTO TOTAL', valor: formatCurrency(totalCusto) },
                  ];

                  exportToPDF(pdfRows, filename, 'HeadCount - Custo por Funcionário', columns, summaryData);
                }

                setExportModalOpen(false);
                setExportSearch('');
                setExpandedExportEmpresa(null);
              }}
              disabled={exporting || selectedEmployees.size === 0}
              className="flex items-center gap-2"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : exportFormat === 'excel' ? (
                <FileSpreadsheet className="h-4 w-4" />
              ) : (
                <FileImage className="h-4 w-4" />
              )}
              {exporting ? 'Exportando...' : `Exportar ${exportFormat === 'excel' ? 'Excel' : 'PDF'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HeadCountPage;
